import React, { useCallback, useRef } from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { Grid, Heading } from '@tq/ui-components';

import { DataTablePagination } from 'components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import {
  showRemoveUserModalState,
  showInviteUserModalState,
  removeUserState,
  manageUsersPageSizeState,
  manageUsersPageNumberState,
} from 'store';
import { ApplicationUser } from 'types';
import { ManageTeamRow, RemoveUserModal } from '.';

export type ManageTeamUsersProps = {
  applicationUsers: ApplicationUser[];
  isAdmin?: boolean;
};

export const ManageTeamUsers: React.FC<ManageTeamUsersProps> = ({
  applicationUsers,
  isAdmin = false,
}) => {
  const { t } = useTranslation('manage_team');
  const isMobile = useIsMobile();
  const pageNumber = useRecoilValue(manageUsersPageNumberState);
  const pageSize = useRecoilValue(manageUsersPageSizeState);
  const showRemoveUserModal = useRecoilValue(showRemoveUserModalState);
  const resetShowRemoveUserModal = useResetRecoilState(showRemoveUserModalState);
  const removeUserData = useRecoilValue(removeUserState);
  const resetRemoveUserState = useResetRecoilState(removeUserState);
  const resetShowInviteUserModal = useResetRecoilState(showInviteUserModalState);

  const tableRef = useRef(null);

  const onClose = () => {
    resetRemoveUserState();
    resetShowRemoveUserModal();
    resetShowInviteUserModal();
  };

  const paginationInfo = useCallback(() => {
    return {
      pageSize,
      currentPage: pageNumber,
      totalCount: applicationUsers.length,
      totalPages: Math.ceil(applicationUsers.length / pageSize),
    };
  }, [applicationUsers, pageNumber, pageSize]);

  const ApplicationUsersPage = useCallback(() => {
    return applicationUsers.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  }, [applicationUsers, pageNumber, pageSize]);

  const columnWidths = isAdmin ? '18% 45% 15% 15%' : '18% 27% 22% 16% 10% 5%';
  const getTableHeaderBackground = isAdmin ? COLOURS.blue[150] : COLOURS.gray[200];
  const getTableHeaderBorderColour = isAdmin ? COLOURS.transparent : COLOURS.gray[300];
  const getGridGap = isAdmin ? { base: 0 } : { base: 4, lg: 2 };
  const getTableHeaderBottomMargin = isAdmin ? 0 : 2;

  return (
    <>
      {!isMobile && (
        <Grid
          mb={getTableHeaderBottomMargin}
          gridTemplateColumns={columnWidths}
          p='1rem 1.5rem'
          bg={getTableHeaderBackground}
          border={`1px solid ${getTableHeaderBorderColour}`}
        >
          <Heading size='sm'>{t('name_label', 'Name')}</Heading>
          <Heading size='sm'>{t('email_label', 'Email')}</Heading>
          {!isAdmin && (
            <>
              <Heading size='sm'>{t('team_label', 'Team')}</Heading>
              <Heading size='sm'>{t('permission_label', 'Permissions')}</Heading>
            </>
          )}
          <Heading size='sm'>{t('registered_label', 'Invitation accepted')}</Heading>
        </Grid>
      )}
      <Grid gridGap={getGridGap} ref={tableRef}>
        {ApplicationUsersPage().map((data) => (
          <ManageTeamRow
            isAdmin={isAdmin}
            key={data.email}
            data={data}
            columnWidths={columnWidths}
          />
        ))}
      </Grid>
      <DataTablePagination
        paginationInfo={paginationInfo()}
        pageNumberState={manageUsersPageNumberState}
        pageSizeState={manageUsersPageSizeState}
        tableRef={tableRef}
        tableScrollHeaderOffset={167}
      />
      {showRemoveUserModal && (
        <RemoveUserModal removeUserData={removeUserData} onClose={onClose} />
      )}
    </>
  );
};
